import React, { useState, useRef, useEffect } from 'react';
import './ChatStyle.css';
import chatIcon from './ChatImages/chat.svg';
import sendIcon from './ChatImages/send.png';
import closeIcon from './ChatImages/close.png';
import restartIcon from './ChatImages/restart.png';
import { MoreOutlined } from '@ant-design/icons';
import { DASHBOARD_CHATBOT, APP_SECURITY_TOKEN } from "../../utils/api";

const ChatPopup = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [messages, setMessages] = useState([{ text: "Ciao! come posso aiutarti?", sender: "bot" }]);
    const [inputValue, setInputValue] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [mode, setMode] = useState(null);
    const chatBodyRef = useRef(null);
    const menuRef = useRef(null);
    const streamControllerRef = useRef(null);

    const resetChat = () => {
        if (streamControllerRef.current) {
            streamControllerRef.current.abort();
        }
        setMessages([{ text: "Ciao! come posso aiutarti?", sender: "bot" }]);
        setIsTyping(false);
        setMenuOpen(false);
        setMode(null);
    };

    const toggleChat = () => {
        setIsOpen(!isOpen);
        setMenuOpen(false);
    };

    const handleFullClose = () => {
        resetChat();
        setIsOpen(false);
    };


    const toggleMenu = (event) => {
        event.stopPropagation();
        setMenuOpen(!menuOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            const isMenuButton = event.target.closest('.menu-button');

            if (menuRef.current && !menuRef.current.contains(event.target) && !isMenuButton) {
                setMenuOpen(false);
            }
        };

        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuOpen]);

    const sendMessage = async () => {
        if (inputValue.trim() === "") return;

        const newMessage = { text: inputValue, sender: "user" };
        setMessages((prevMessages) => [...prevMessages, newMessage]);
        setInputValue("");

        setIsTyping(true);

        try {
            const body = JSON.stringify({
                session_id: sessionStorage.getItem("username"),
                mode: mode,
                message: inputValue,
                stream: true
            });

            console.log("Request body:", body);

            const controller = new AbortController();
            streamControllerRef.current = controller;

            const response = await fetch(DASHBOARD_CHATBOT, {
                method: "POST",
                headers: {
                    accept: "application/x-ndjson",
                    "Content-Type": "application/json",
                    "X-Application-Token": APP_SECURITY_TOKEN,
                },
                body: body,
                signal: controller.signal,
            });


            if (!response.ok || !response.body) {
                const errorDetails = await response.text();
                throw new Error(`Errore HTTP: ${response.status}, Dettagli: ${errorDetails}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let fullMessage = "";

            setMessages((prevMessages) => [...prevMessages, { text: "", sender: "bot" }]);

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split("\n").filter(line => line.trim());

                for (const line of lines) {
                    try {
                        const json = JSON.parse(line);
                        if (json?.content !== undefined) {
                            fullMessage += json.content;
                            setMessages((prevMessages) => {
                                const updated = [...prevMessages];
                                const lastIndex = updated.length - 1;
                                if (updated[lastIndex].sender === "bot") {
                                    updated[lastIndex] = { ...updated[lastIndex], text: fullMessage };
                                }
                                return updated;
                            });
                        }

                        if (json?.done) {
                            setIsTyping(false);
                            if (json?.source_documents) {
                                console.log("Source documents:", json.source_documents);
                            }
                        }

                    } catch (err) {
                        console.warn("Failed to parse chunk:", line, err);
                    }
                }
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log("Stream interrupted");
            } else {
                console.error("Errore:", error);
                setMessages((prevMessages) => [
                    ...prevMessages,
                    { text: "Errore durante la connessione al server.", sender: "bot" },
                ]);
            }
        }

    };

    useEffect(() => {
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
    }, [messages, isTyping]);

    return (
        <>
            {isOpen && <div className="chat-overlay" onClick={toggleChat}></div>}

            <div className="chat-button" onClick={toggleChat}>
                <img src={chatIcon} alt="Chat Icon" className="chat-icon" />
            </div>

            {isOpen && (
                <div className="chat-popup">
                    <div className="chat-header">
                        <h3>Syntphony Chat</h3>
                        <button className="menu-button" onClick={(e) => toggleMenu(e)}>
                            <MoreOutlined />
                        </button>

                        {menuOpen && (
                            <div className="chat-menu" ref={menuRef}>
                                <button onClick={handleFullClose}><img src={closeIcon} className="close-icon" alt="Close Icon" /> Chiudi</button>
                                <button onClick={resetChat}><img src={restartIcon} className="restart-icon" alt="Restart Icon" /> Ricomincia</button>
                            </div>
                        )}
                    </div>

                    <div className="chat-body" ref={chatBodyRef}>

                        {messages.map((msg, index) => (
                            <div key={index} className={`chat-message ${msg.sender}`}>
                                {msg.text}
                            </div>
                        ))}
                        {isTyping && (
                            <div className="chat-message bot typing">
                                <span className="dot"></span>
                                <span className="dot"></span>
                                <span className="dot"></span>
                            </div>
                        )}
                        {mode === null && (
                            <div className="chat-message user mode-selector">
                                <div className="mode-buttons">
                                    <button onClick={() => setMode("rag")}>Informazioni sulla dashboard</button>
                                    <button onClick={() => setMode("query")}>Informazioni sulle transazioni</button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="chat-footer">
                        <input
                            type="text"
                            placeholder={mode ? "Fai una domanda" : "Seleziona una modalità"}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && mode && sendMessage()}
                            disabled={!mode}
                        />
                        <button onClick={() => mode && sendMessage()} disabled={!mode}>
                            <img src={sendIcon} className="send-icon" alt="Send Icon" />
                        </button>

                    </div>
                </div>
            )}
        </>
    );
};

export default ChatPopup;